import { useSelector as reduxUseSelector } from 'react-redux';
import { get, isEqual, find } from 'lodash';
import usePreviousHook from 'containers/App/hooks/usePrevious';

import {
  makeSurveyIsUnderEdit,
  makeSelectSurveyType,
} from 'containers/Survey/selectors';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';

import { featureCleaner } from 'containers/TargetingPeeqPage/helpers';
import {
  makeSelectAudiences,
  makeSelectTargetingVersion,
  makeSelectHasFetched,
  makeSelectCountryId,
} from 'containers/TargetingPeeqPage/selectors';

let targetingData;
let previousSurveyId;

function useGetTargetingPeeqData(
  { surveyId, overrideSurveyType },
  { useSelector = reduxUseSelector, usePrevious = usePreviousHook } = {},
) {
  const selectedCriteriaPerAudience = useSelector(makeSelectAudiences);
  let activeAudienceRefId;
  if (selectedCriteriaPerAudience.length > 0) {
    activeAudienceRefId = (
      find(selectedCriteriaPerAudience, ['isActive', true]) ||
      get(selectedCriteriaPerAudience, '[0]', {})
    ).refId;
  }
  const countryId = useSelector(makeSelectCountryId(activeAudienceRefId));
  const targetingEditVersion = useSelector(makeSelectTargetingVersion);
  const surveyIsUnderEdit = useSelector(makeSurveyIsUnderEdit);
  const surveyType = useSelector(makeSelectSurveyType);

  const prevselectedCriteriaPerAudience = usePrevious(
    selectedCriteriaPerAudience,
  );
  const prevTargetingEditVersion = usePrevious(targetingEditVersion);
  const prevCountryId = usePrevious(countryId);

  const hasFetched = useSelector(makeSelectHasFetched);

  if (
    surveyType !== SURVEY_TYPE_PEEQ &&
    overrideSurveyType !== SURVEY_TYPE_PEEQ
  ) {
    return {};
  }

  if (!hasFetched) {
    return { shouldUpdateTargeting: false };
  }

  if (previousSurveyId !== surveyId) {
    targetingData = undefined;
    previousSurveyId = surveyId;
  }

  const shouldUpdateTargeting =
    targetingEditVersion !== prevTargetingEditVersion &&
    targetingEditVersion !== -1 &&
    prevTargetingEditVersion !== -1;

  if (!shouldUpdateTargeting && targetingData) {
    return {
      ...targetingData,
      shouldUpdateTargeting,
      feasibilityAffected: false,
      costAffected: false,
      previousVersion: prevTargetingEditVersion,
      version: targetingEditVersion,
    };
  }

  const cleanFeaturesPerAudience = selectedCriteriaPerAudience.map(
    ({ criteria, ...audience }) => ({
      ...audience,
      features: featureCleaner(criteria),
    }),
  );

  // eslint-disable-next-line
  let cleanPreviousFeaturesPerAudience = [];
  if (prevselectedCriteriaPerAudience) {
    cleanPreviousFeaturesPerAudience = prevselectedCriteriaPerAudience.map(
      ({ criteria, ...audience }) => ({
        ...audience,
        features: featureCleaner(criteria),
      }),
    );
  }

  const feasibilityAffected = !isEqual(
    cleanFeaturesPerAudience,
    cleanPreviousFeaturesPerAudience,
  );

  const costAffected = prevCountryId !== countryId || feasibilityAffected;

  targetingData = {
    targeting: {
      audiences: cleanFeaturesPerAudience,
      countryId,
    },
    previousVersion: prevTargetingEditVersion,
    version: targetingEditVersion,
    feasibilityAffected,
    costAffected,
    isRevision: surveyIsUnderEdit,
    shouldUpdateTargeting,
  };

  return targetingData;
}

export default useGetTargetingPeeqData;
